import { NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    Output,
    TemplateRef,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';

import { OfferSource } from '@cds';
import { DisplayType } from '@cds/betting-offer';
import { OptionGroupType, OptionGroupTypeService } from '@frontend/sports/betting-offer/feature/fixture-factories';
import {
    BetBuilderButtonSource,
    CrmPromoIdsMap,
    EventModel,
    EventOptionGroup,
    EventOptionGroupType,
} from '@frontend/sports/betting-offer/feature/model';
import {
    BetBuilderConfig,
    EpcotConfig,
    EventDetailsConfig,
    MarketGroupingConfig,
    MarketHelpConfig,
    PriceBoostConfig,
    Sitecore,
} from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterButtonSize } from '@frontend/sports/common/core/feature/scroll-adapter';
import { NumberDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { ThemeBorder, ThemeOptions, injectCurrentTheme } from '@frontend/sports/common/ui/shared';
import { AccordionVariant, SportsThemedAccordionDirective } from '@frontend/sports/common/ui/sports-themed-accordion';
import { SportsThemedCardDirective } from '@frontend/sports/common/ui/sports-themed-card';
import { EVENT_DETAILS_MODAL_CONTEXT } from '@frontend/sports/event-details/modal/feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { trackingConstants } from '@frontend/sports/tracking/feature';
import { PerformanceTrackerComponent } from '@frontend/sports/tracking/feature/performance-tracker';
import { OptionPanelThreshold } from '@frontend/sports/types/configuration';
import { TabBarEvent } from '@frontend/sports/web/tab-bar-utils';
import { DsAccordionModule } from '@frontend/ui/accordion';
import { DsBadge, DsBadgeVariant } from '@frontend/ui/badge';
import { DsButton } from '@frontend/ui/button';
import { DsCard } from '@frontend/ui/card';
import { DeviceService, WINDOW } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Store } from '@ngrx/store';
import { RxIf } from '@rx-angular/template/if';
import { BehaviorSubject } from 'rxjs';

import { BadgeComponent } from '../crm-promotion/badge.component';
import { OptionMarketSetData } from '../event-details-common/event-details-common.models';
import { EventDetailsService } from '../event-details-common/event-details.service';
import { OptionSheetViewActions } from '../sheet-view-core/sheet-view.actions';
import { EventTimerComponent } from '../timers/event-timer.component';
import { BetBuilderCorrectScoreOptionGroupComponent } from './bet-builder-correct-score-option-group.component';
import { BetBuilderPlayersOptionGroupComponent } from './bet-builder-goalscorer-option-group.component';
import { BetBuilderOverUnderOptionGroupComponent } from './bet-builder-over-under-option-group.component';
import { BetBuilderSixPackComponent } from './bet-builder-six-pack.component';
import { BetBuilderSpreadOptionGroupComponent } from './bet-builder-spread-option-group.component';
import { BetBuilderStandardGroupComponent } from './bet-builder-standard-group.component';
import { BetBuilderPlayerTeamOptionGroupComponent } from './bet-builder-teams-option-group.component';
import { CorrectScoreOptionGroupToggleViewService } from './correct-score-option-group-toggle-view.service';
import { CorrectScoreOptionGroupWrapperComponent } from './correct-score-option-group-wrapper.component';
import { EachWayMarketHelperService } from './each-way-market-helper.service';
import { GoalScorerGroupComponent } from './goal-scorer-group.component';
import { HeadToHeadOptionGroupComponent } from './head-to-head-option-group.component';
import { MarketHelpToggleComponent } from './market-help/market-help-toggle.component';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionGroupTrackingService } from './option-group-tracking.service';
import { OptionPanelHeaderComponent } from './option-panel-header.component';
import { OutrightsOptionGroupComponent } from './outrights-option-group.component';
import { OverUnderOptionGroupComponent } from './over-under-option-group.component';
import { PeriodOptionGroupComponent } from './period-option-group.component';
import { PlayerCombinedOptionGroupComponent } from './player-combined-option-group.component';
import { PlayerMilestoneOptionGroupComponent } from './player-milestone-option-group.component';
import { PlayerMilestoneOverUnderOptionGroupComponent } from './player-milestone-over-under-option-group.component';
import { PlayerOutcomeOptionGroupComponent } from './player-outcome-option-group.component';
import { PlayerPropsOptionGroupComponent } from './player-props-option-group.component';
import { PlayerStatsOptionGroupComponent } from './player-stats-option-group.component';
import { PrecreatedBabOptionGroupComponent } from './precreated-bab-option-group.component';
import { RegularGroupComponent } from './regular-group.component';
import { ScoreMarketOptionGroupComponent } from './score-market-option-group.component';
import { ScorecastOptionGroupComponent } from './scorecast/scorecast-option-group.component';
import { WincastOptionGroupComponent } from './scorecast/wincast/wincast-option-group.component';
import { WincastTwoWayScorerOptionGroupComponent } from './scorecast/wincast/wincast-two-way-scorer-option-group.component';
import { SixPackOptionGroupComponent } from './six-pack-option-group.component';
import { SpreadOptionGroupComponent } from './spread-option-group.component';
import { TotalScoreOptionGroupComponent } from './total-score-option-group.component';
import { YesNoOptionGroupComponent } from './yes-no-option-group.component';

@Component({
    selector: 'ms-option-panel',
    templateUrl: './option-panel.component.html',
    styleUrl: './option-panel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RxIf,
        BadgeComponent,
        OptionPanelHeaderComponent,
        PerformanceTrackerComponent,
        MarketHelpComponent,
        PlayerCombinedOptionGroupComponent,
        PlayerOutcomeOptionGroupComponent,
        PlayerMilestoneOptionGroupComponent,
        PlayerMilestoneOverUnderOptionGroupComponent,
        PrecreatedBabOptionGroupComponent,
        PeriodOptionGroupComponent,
        ScorecastOptionGroupComponent,
        WincastOptionGroupComponent,
        RegularGroupComponent,
        CorrectScoreOptionGroupWrapperComponent,
        OverUnderOptionGroupComponent,
        GoalScorerGroupComponent,
        PlayerPropsOptionGroupComponent,
        SpreadOptionGroupComponent,
        WincastTwoWayScorerOptionGroupComponent,
        SixPackOptionGroupComponent,
        ScoreMarketOptionGroupComponent,
        OutrightsOptionGroupComponent,
        YesNoOptionGroupComponent,
        TotalScoreOptionGroupComponent,
        HeadToHeadOptionGroupComponent,
        BetBuilderStandardGroupComponent,
        BetBuilderSixPackComponent,
        BetBuilderOverUnderOptionGroupComponent,
        BetBuilderCorrectScoreOptionGroupComponent,
        BetBuilderSpreadOptionGroupComponent,
        BetBuilderPlayerTeamOptionGroupComponent,
        BetBuilderPlayersOptionGroupComponent,
        PlayerStatsOptionGroupComponent,
        NgTemplateOutlet,
        DsCard,
        DsAccordionModule,
        MarketHelpToggleComponent,
        IconCustomComponent,
        DsBadge,
        SportsThemedAccordionDirective,
        SportsThemedCardDirective,
        EventTimerComponent,
        DsButton,
    ],
    providers: [CorrectScoreOptionGroupToggleViewService],
})
export class OptionPanelComponent implements AfterViewInit, OnChanges {
    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() promoted: boolean;
    @Input() crmPromoted: boolean;
    @Input() optionGroup: EventOptionGroup;
    @Input() preselectedPick: { optionId: number } | null;
    @Input() thresholds?: OptionPanelThreshold[];
    @Input() scrollAdapterButtonSize: ScrollAdapterButtonSize;
    @Input() crmPromoIdsMap: CrmPromoIdsMap = { markets: {}, options: {}, templateIds: {} };
    @Input() toggleable = true;
    @Input() showHelp = false;
    @Input() collapsed = false;
    @Input() eventHelpText?: string;
    @Input() showEventName = false;
    @Input() showMarketHelp = false;
    @Input() showEventTimer = false;
    @Input() isLinkToEvent = true;
    @Input() showEventChevron = false;
    @Input() optionGroupLinkable = false;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() showMoreCounter = false;
    @Input() optionsLimitOverride?: number;
    @Input() babButtonSource?: BetBuilderButtonSource;
    @Input() betBuilderEpcotEnabled = false;
    @Input() calendarTitle?: string;
    @Input() hideMarketHeader: boolean;
    @Input() showSixPackLogosColor: boolean;
    @Input() showSixPackLogos: boolean;
    @Input() selectedSetId: string | number;
    @Input() isInSheetView = false;
    @Input() marketLogoShow = false;
    @Input() hideEventCarousel?: boolean;
    @Input() appendTooltipToWidgetSlot = false;
    customOptionPanelBodyTemplate = input<TemplateRef<{ showMarketHelp: boolean }>>();
    customOptionPanelHeaderTemplate = input<TemplateRef<void>>();
    isBorderHidden = input(true);
    @Output() collapsedChange = new EventEmitter<boolean>();
    @Output() eventTitleClicked = new EventEmitter<EventModel>();
    @Output() closeBtnClicked = new EventEmitter();
    @Output() activePeriodChange = new EventEmitter<TabBarEvent>();
    hideOptions = input<boolean>();
    readonly isBadgeEnabled = output<void>();
    optionMarketSetData = input<OptionMarketSetData>();

    @HostBinding('class') className = 'option-panel';

    @HostBinding('class.precreated-bab-tab') get IsPrecreatedBabTab(): boolean {
        return this.isPrecreatedBabTab;
    }

    strategy$ = new BehaviorSubject('native');

    groupTypes = OptionGroupType;
    groupType: OptionGroupType;
    isPrecreatedBabTab = false;
    hideBetBuilderButton = false;
    activePeriodTab?: string;
    showPlayerStats = false;

    private readonly modalInjector = inject(EVENT_DETAILS_MODAL_CONTEXT).injector;
    isPrecreatedBuildABet: boolean;
    showPrecreatedBetBuilderNewBadge = false;
    isPrecreatedBetBuilderBadgeEnabled: boolean;
    winMarketName?: string;
    showMarketHelpToggle: boolean;
    isPointed = signal(false);
    private currentTheme = injectCurrentTheme();
    private typeHelper = inject(OptionGroupTypeService);
    private elementRef = inject(ElementRef);
    private optionGroupTrackingService = inject(OptionGroupTrackingService);
    private eventDetailsService = inject(EventDetailsService);
    private eventDetailsConfig = inject(EventDetailsConfig);
    private priceBoostConfig = inject(PriceBoostConfig);
    private store = inject(Store);
    public sitecore = inject(Sitecore);
    public betBuilderConfig = inject(BetBuilderConfig);
    public deviceService = inject(DeviceService);
    private eachWayMarketHelperService = inject(EachWayMarketHelperService);
    private marketHelpConfig = inject(MarketHelpConfig);
    private redirectHelperService = inject(RedirectHelperService);
    private readonly epcotConfig = inject(EpcotConfig);
    private marketGroupingConfig = inject(MarketGroupingConfig);
    private readonly _window = inject(WINDOW);

    isEpcotEnabled = this.epcotConfig.isEnabled;
    showSuspendedSelection =
        this.eventDetailsConfig.isSuspendedSelectionVisibilityEnabled || this.priceBoostConfig.isEnabled || this.priceBoostConfig.isEnabledAPB;
    private marketGroupsWithNewBadge: NumberDictionary<string[]> = {};

    readonly newBadgeThemeVariant: ThemeOptions<DsBadgeVariant> = {
        'sports-interaction': 'blue-strong',
    };
    readonly themeBorder: ThemeBorder = {
        'mgm-dice': { hasBorder: true },
        'sports-interaction': { hasBorder: true },
    };

    constructor() {
        this.marketGroupingConfig.marketGroupsWithNewBadge.forEach((x) => {
            this.marketGroupsWithNewBadge[x.sportId] = x.marketGroupItemIds;
        });
    }

    ngOnChanges(changes: ISimpleChanges<OptionPanelComponent>): void {
        if (changes.optionGroup) {
            this.groupType = this.typeHelper.getOptionGroupType(this.optionGroup);
            this.isPrecreatedBabTab = this.eventDetailsService.isPreCreatedBABTab(this.event, this.selectedSetId);

            if (this.isPrecreatedBabTab) {
                this.hideMarketHeader = true;
                this.hideBetBuilderButton = true;
                this.crmPromoted = false;
                this.showMarketHelp = false;
            }

            if (!this.isInSheetView) {
                this.store.dispatch(OptionSheetViewActions.updateOptionSheetView({ payload: { sourceOptionGroup: this.optionGroup } }));
            }
        }

        this.isPrecreatedBuildABet = this.groupType === OptionGroupType.PrecreatedBuildABet;

        if (this.isPrecreatedBuildABet && this.sitecore.betBuilder.messages) {
            this.showPrecreatedBetBuilderNewBadge = Boolean(
                this.event.sport?.id && this.betBuilderConfig.isPrecreatedBetBuilderNewBadgeEnabledForSport?.includes(this.event.sport.id),
            );

            const themedTabName = this.betBuilderConfig.themedTabs[this.event.sport.id]?.find(
                (tab) => tab.key?.toUpperCase() === this.optionGroup.periodName?.toUpperCase(),
            )?.name;

            this.optionGroup.name =
                !this.optionGroup.subGroups && themedTabName?.trim()
                    ? `${this.sitecore.betBuilder.messages.PrecreatedBuildABetTitle} - ${themedTabName}`
                    : this.sitecore.betBuilder.messages.PrecreatedBuildABetTitle;
        }

        this.isPrecreatedBetBuilderBadgeEnabled = this.betBuilderConfig.isPrecreatedBetBuilderBadgeEnabled;
        this.winMarketName = this.eachWayMarketHelperService.getWinMarketName(this.optionGroup, this.event);
        this.setMarketHelpToggle();

        if ((changes.optionGroup || changes.event) && !(this.optionGroup.source === OfferSource.V2 && !this.eventDetailsConfig.enableV2LiveStats)) {
            this.showPlayerStats =
                this.event.live &&
                !!this.event.playerStats?.some((x) => x.source === this.optionGroup.source) &&
                !!this.optionGroup.detailedGrouping?.playerStatsProps?.length &&
                !this.isTeamStats(this.optionGroup);
        }

        this.setOptionGroupName();
    }

    themedAccordionVariant: AccordionVariant = {
        'sportingbet-2': 'surface-low',
        'betboo-br': 'surface-low',
        'bwin-dark': 'surface-low',
        'mgm-dice': 'surface-low',
        'sports-interaction': 'surface-highest',
    };

    get shouldShowInlineHelpText(): boolean {
        const groupId = this.activeOptionGroup.detailedGrouping?.marketGroupItemId;
        return !!(
            this.optionGroup.detailedGrouping?.marketHelpPath &&
            groupId &&
            this.marketGroupingConfig.showMarketHelpTextAsBlurb?.[this.event.sport.id]?.includes(groupId)
        );
    }

    get activeOptionGroup(): EventOptionGroup {
        if (Array.isArray(this.optionGroup.subGroups) && this.activePeriodTab) {
            return this.optionGroup.subGroups.find((g) => g.originalId === this.activePeriodTab) || this.optionGroup;
        }
        return this.optionGroup;
    }

    private isTeamStats(optionGroup: EventOptionGroup): boolean {
        return !!optionGroup.detailedGrouping?.playerStatsProps?.[0]?.split('.')[1];
    }

    ngAfterViewInit() {
        this.strategy$.next('normal');
    }

    get newBadgeVariant(): DsBadgeVariant {
        return this.newBadgeThemeVariant[this.currentTheme] ?? 'blue';
    }

    readonly hasNewBadge = computed<boolean>(() => {
        if (!this.optionGroup.detailedGrouping?.marketGroupItemId) {
            return false;
        }
        return (this.marketGroupsWithNewBadge[this.event.sport.id] || []).includes(this.optionGroup.detailedGrouping.marketGroupItemId);
    });

    goToEvent(event$: Event): void {
        event$.stopPropagation();
        if (this.isLinkToEvent) {
            this.redirectHelperService.goToEvent(this.event);
        }
    }

    isBadgeclicked(): void {
        this.isBadgeEnabled.emit();
    }

    handleHeaderClick(): void {
        if (this.toggleable) {
            this.collapsed = !this.collapsed && (this.showSuspendedSelection || this.optionGroup.online);
            this.collapsedChange.emit(this.collapsed);
            this.setShowMarketHelp(false);
        }
    }

    onHeaderToggle(collapsed: boolean): void {
        this.collapsed = collapsed;
        this.collapsedChange.emit(this.collapsed);
        this.optionGroupTrackingService.trackCollapseExpandClick(
            this.event.sport.name,
            collapsed ? 'collapse' : 'expand',
            this.event.participants,
            this.optionGroup.name,
            this.optionGroup.name,
        );
    }

    onEventTitleClick(): void {
        this.eventTitleClicked.emit();
    }

    onExpandedClicked(isExpanded: boolean): void {
        if (!isExpanded && !this.isFullyVisible()) {
            this.optionPanel?.scrollIntoView(true);
        }
    }

    onCloseButtonClicked(): void {
        this.closeBtnClicked.emit();
    }

    onActivePeriodChange(activeTab$: TabBarEvent): void {
        this.activePeriodTab = activeTab$.active.attribute;
        this.activePeriodChange.emit(activeTab$);
    }

    isFullyVisible(): boolean {
        const { top, bottom } = this.optionPanel?.getBoundingClientRect() ?? {};

        return top >= 0 && bottom <= this._window.innerHeight;
    }

    private get optionPanel(): HTMLElement {
        return this.elementRef.nativeElement;
    }

    openSheetView(): void {
        this.optionGroupTrackingService.trackEvents({
            label: this.event.sport.name,
            actionEvent: trackingConstants.EVENT_CLICK,
            participants: this.event.participants,
            locationEvent: this.getLocationEvent(),
            eventDetailText: trackingConstants.SEE_ALL,
            event: trackingConstants.EVENT_TRACKING,
            sportId: this.event.sport.id,
            leagueId: this.event.league.id,
        });

        this.store.dispatch(
            OptionSheetViewActions.openOptionSheetView({
                payload: {
                    showMarketHelp: this.showMarketHelp,
                    sourceEvent: this.event,
                    sourceOptionGroup: this.optionGroup,
                    promoted: this.promoted,
                    crmPromoted: this.crmPromoted,
                    crmPromoIdsMap: this.crmPromoIdsMap,
                    tracking: this.tracking,
                    showMoreCounter: this.showMoreCounter,
                    optionsLimitOverride: this.optionsLimitOverride,
                    showHelp: this.showHelp,
                    thresholds: this.thresholds,
                    trackingModuleType: this.trackingModuleType,
                    betBuilderEpcotEnabled: this.betBuilderEpcotEnabled,
                    isInSheetView: true,
                    sourceOfPick: this.sourceOfPick,
                    showEventName: this.showEventName,
                    showEventTimer: this.showEventTimer,
                    toggleable: this.toggleable,
                    isLinkToEvent: this.isLinkToEvent,
                    showEventChevron: this.showEventChevron,
                    babButtonSource: this.babButtonSource,
                    hideMarketHeader: this.hideMarketHeader,
                    showSixPackLogosColor: this.showSixPackLogosColor,
                    showSixPackLogos: this.showSixPackLogos,
                    selectedSetId: this.selectedSetId,
                    groupType: this.groupType,
                    hideEventCarousel: this.hideEventCarousel,
                    optionMarketSetData: this.optionMarketSetData(),
                },
                context: {
                    injector: this.modalInjector,
                },
            }),
        );
    }

    getLocationEvent(): string {
        let locationEventInfo = this.optionGroup.name;
        const trackingInfo = this.tracking?.split('/')[0];
        if (trackingInfo) {
            locationEventInfo = `${trackingInfo}-${locationEventInfo}`;
        }

        return locationEventInfo;
    }

    private setMarketHelpToggle(): void {
        if (this.eventHelpText) {
            this.showMarketHelpToggle = true;

            return;
        }
        this.showMarketHelpToggle = this.showHelp && this.marketHelpConfig.enabled;

        if (!this.showMarketHelpToggle) {
            return;
        }

        if (this.groupType === OptionGroupType.Period && this.optionGroup.subGroups) {
            this.showMarketHelpToggle = this.optionGroup.subGroups.some((group) => this.hasHelpPath(group));
        } else {
            this.showMarketHelpToggle = this.hasHelpPath(this.optionGroup);
        }
    }

    toggleMarketHelp(value: boolean): void {
        this.setShowMarketHelp(value);
    }

    private setShowMarketHelp(value: boolean): void {
        this.showMarketHelp = value;
    }

    private hasHelpPath(optionGroup: EventOptionGroup): boolean {
        return !!optionGroup.detailedGrouping && !!optionGroup.detailedGrouping.marketHelpPath;
    }

    private setOptionGroupName(): void {
        // For PlayerProps display type with V1 offer source show the period tab as market header
        if (
            this.event.offerSource === OfferSource.V1 &&
            this.optionGroup.detailedGrouping.displayType === DisplayType.PlayerProps &&
            this.optionGroup.periodName &&
            !this.optionGroup.hasSubGroups(EventOptionGroupType.Period)
        ) {
            this.optionGroup.name = this.optionGroup.periodName;
        }
    }
}
